import type { CapabilityState, CloudflareServiceToken } from "../shared/types";
import { CapabilityBadge, EmptyState } from "./AdminUi";
import { PaginationControls, usePagination } from "./pagination";

export function CloudflareServiceTokensSection({
  capability,
  onCreateToken,
  onDeleteToken,
  onRefreshToken,
  onRotateToken,
  onToggleToken,
  serviceTokens,
}: {
  capability: CapabilityState;
  onCreateToken: () => void;
  onDeleteToken: (token: CloudflareServiceToken) => void;
  onRefreshToken: (token: CloudflareServiceToken) => void;
  onRotateToken: (token: CloudflareServiceToken) => void;
  onToggleToken: (token: CloudflareServiceToken) => void;
  serviceTokens: CloudflareServiceToken[];
}) {
  const [pageSlice, setPage] = usePagination(serviceTokens, 10);
  return (
    <section className="directory-card">
      <header className="collection-header">
        <div>
          <h2>Service credentials</h2>
          <p>Machine identities only. Secrets never appear in this view.</p>
        </div>
        <div className="collection-header__actions">
          <CapabilityBadge capability={capability} />
          <button
            className="button button--primary button--compact"
            onClick={onCreateToken}
            type="button"
          >
            Create token
          </button>
        </div>
      </header>
      <div className="table-wrap">
        <table className="cloudflare-service-table">
          <caption className="sr-only">
            Cloudflare Access service tokens
          </caption>
          <thead>
            <tr>
              <th>Credential</th>
              <th>Status</th>
              <th>Expires</th>
              <th>
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {pageSlice.items.map((token) => (
              <tr key={token.id}>
                <td data-label="Credential">
                  <strong>{token.name}</strong>
                  <small>{token.clientId}</small>
                </td>
                <td data-label="Status">
                  <span
                    className={`status status--${token.enabled ? "active" : "disabled"}`}
                  >
                    <i /> {token.enabled ? "Enabled" : "Disabled"}
                  </span>
                </td>
                <td data-label="Expires">{token.expiresAt ?? "Forever"}</td>
                <td data-label="Actions">
                  <div className="table-actions table-actions--wrap">
                    <button
                      className="button button--compact button--secondary"
                      onClick={() => onRotateToken(token)}
                      type="button"
                    >
                      Rotate
                    </button>
                    <button
                      className="button button--compact button--secondary"
                      onClick={() => onRefreshToken(token)}
                      type="button"
                    >
                      Renew
                    </button>
                    <button
                      className={`button button--compact ${token.enabled ? "button--secondary" : "button--primary"}`}
                      onClick={() => onToggleToken(token)}
                      type="button"
                    >
                      {token.enabled ? "Disable" : "Enable"}
                    </button>
                    <button
                      className="button button--compact button--danger-quiet"
                      onClick={() => onDeleteToken(token)}
                      type="button"
                    >
                      Revoke
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!serviceTokens.length && (
          <EmptyState
            compact
            hint="Create credentials in Cloudflare for non-browser clients."
            title="No service credentials loaded"
          />
        )}
      </div>
      <PaginationControls
        label="service credentials"
        onPageChange={setPage}
        slice={pageSlice}
      />
    </section>
  );
}
